import { OFFER_MERGE_FIELDS, type OfferLetterPreviewRequest, type OfferLetterView, type OfferMergeField } from '@nest-recruit/shared';

// ADMIN-16 (Admin redesign 2026-11-02, Option A of 4-messages/offer-letters.html): what a letter's form holds, what has
// changed, the "Insert a field" list, and the preview's request. Pure, so each rule is tested without a browser.

export interface LetterForm {
  readonly name: string;
  readonly ventureId: string | null;
  readonly signatoryName: string;
  readonly signatoryDesignation: string;
  readonly body: string;
  readonly isActive: boolean;
}

/** The form's values as `useWatch` gives them: a part may be missing while its field mounts. */
export type WatchedLetter = Partial<LetterForm>;

/** The starting wording of a new letter: every §L12 field in a plain business letter. */
export const STARTER_BODY = [
  'Dear {{candidateName}},',
  'We are pleased to offer you the position of {{jobTitle}} in {{department}}, {{venture}}, on a {{employmentType}} basis, based at {{workLocation}}.',
  'Your gross monthly salary will be {{grossMonthlySalary}} ({{salaryBreakdown}}). You will join on {{joiningDate}} and report to {{reportingManager}}. Your probation period is {{probationPeriod}}, and the notice period is {{noticePeriod}}.',
  'This offer ({{offerReference}}, issued {{issueDate}}) is open until {{offerValidUntil}}. Please accept or decline it using the link in our email.',
  '{{signatoryName}}\n{{signatoryDesignation}}',
].join('\n\n');

export const NEW_LETTER: LetterForm = { name: '', ventureId: null, signatoryName: '', signatoryDesignation: '', body: STARTER_BODY, isActive: true };

export function formOf(letter: OfferLetterView): LetterForm {
  return {
    name: letter.name,
    ventureId: letter.ventureId,
    signatoryName: letter.signatoryName,
    signatoryDesignation: letter.signatoryDesignation,
    body: letter.body,
    isActive: letter.isActive,
  };
}

const FIELD_LABELS: readonly { readonly key: keyof LetterForm; readonly label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'ventureId', label: 'Venture' },
  { key: 'signatoryName', label: 'Signed by' },
  { key: 'signatoryDesignation', label: 'Designation' },
  { key: 'body', label: 'The letter' },
  { key: 'isActive', label: 'Jobs can choose' },
];

/** One part, the way the saved value compares: text trimmed, as the server keeps it. */
function comparable(values: WatchedLetter, key: keyof LetterForm): string {
  const value = values[key];
  if (typeof value === 'string') return value.trim();
  return JSON.stringify(value ?? null);
}

/** The parts that differ from `base`, by the page's own names. */
export function changedLetterParts(values: WatchedLetter, base: WatchedLetter): readonly string[] {
  return FIELD_LABELS.filter(({ key }) => comparable(values, key) !== comparable(base, key)).map(({ label }) => label);
}

/** Someone else saved this letter meanwhile: their version is the base, and what this Admin changed stays theirs. */
export function rebasedLetter(mine: LetterForm, was: LetterForm, now: LetterForm): LetterForm {
  const keep = <K extends keyof LetterForm>(key: K): LetterForm[K] => (comparable(mine, key) === comparable(was, key) ? now[key] : mine[key]);
  return {
    name: keep('name'),
    ventureId: keep('ventureId'),
    signatoryName: keep('signatoryName'),
    signatoryDesignation: keep('signatoryDesignation'),
    body: keep('body'),
    isActive: keep('isActive'),
  };
}

/** What Save sends: trimmed, as the server keeps it. */
export function savedLetterOf(values: LetterForm): LetterForm {
  return {
    name: values.name.trim(),
    ventureId: values.ventureId,
    signatoryName: values.signatoryName.trim(),
    signatoryDesignation: values.signatoryDesignation.trim(),
    body: values.body.trim(),
    isActive: values.isActive,
  };
}

/** The preview asks only for what the page prints. Null `offerId`: sample values. */
export function previewRequestOf(values: WatchedLetter, offerId: string | null): OfferLetterPreviewRequest {
  return {
    letter: {
      ventureId: values.ventureId ?? null,
      signatoryName: (values.signatoryName ?? '').trim(),
      signatoryDesignation: (values.signatoryDesignation ?? '').trim(),
      body: values.body ?? '',
    },
    ...(offerId ? { offerId } : {}),
  };
}

export interface FieldChoice {
  readonly key: OfferMergeField;
  readonly label: string;
}

/** "Insert a field": every §L12 field whose name or key holds what was typed, in the catalogue's order. */
export function matchingFields(query: string): readonly FieldChoice[] {
  const wanted = query.trim().toLowerCase().replace(/\s+/g, ' ');
  if (wanted === '') return OFFER_MERGE_FIELDS;
  return OFFER_MERGE_FIELDS.filter((field) => field.label.toLowerCase().includes(wanted) || field.key.toLowerCase().includes(wanted.replace(/ /g, '')));
}

/** `{{field}}` put where the cursor was (or over what was selected), and where the cursor goes after it. */
export function insertField(body: string, start: number, end: number, key: OfferMergeField): { readonly body: string; readonly cursor: number } {
  const from = Math.max(0, Math.min(start, body.length));
  const to = Math.max(from, Math.min(end, body.length));
  const token = `{{${key}}}`;
  return { body: `${body.slice(0, from)}${token}${body.slice(to)}`, cursor: from + token.length };
}
